import React, {memo, useEffect, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useIncidentsQuery, useIncidentRegistryQuery} from "../query";
import {getCoreRowModel, useReactTable} from "@tanstack/react-table";
import DataTable from "../../../../components/DataTable";
import {format, parseISO} from "date-fns";
import useDataTable from "../../../../components/DataTable/useDataTable";
import {formatFilterForAPI} from "../useFeaturesState";
import Tooltip from "@mui/material/Tooltip";
import FeatureSelectionChips from "../Panels/FeatureSelectionChips";
import MDBox from "../../../../materialDashboard/components/MDBox";

const areEqualCellValues = (prevProps, nextProps) => {
    const prevValue = prevProps.cell.getValue();
    const nextValue = nextProps.cell.getValue();
    return prevValue === nextValue;
}

const IncidentCategory = memo(({cell}) => {
    const value = cell.getValue();
    const incidentRegistry = cell.table.options.meta.incidentRegistry;
    return incidentRegistry?.incidentCategoryV2.find(cat => cat.code === value)?.description;
}, areEqualCellValues)

const IncidentSubcategory = memo(({cell}) => {
    const value = cell.getValue();
    const incidentRegistry = cell.table.options.meta.incidentRegistry;
    return incidentRegistry?.incidentSubcategory.find(cat => cat.code === value)?.description;
}, areEqualCellValues)

const NoteCell = memo(({cell}) => {
    const value = cell.getValue();
    if (value !== "" && value != null) {
        return <Tooltip title={value} placement="bottom" disableInteractive>
            <span>{value.length > 16 ? value.substring(0, 15) + "..." : value}</span>
        </Tooltip>
    } else {
        return null;
    }
}, areEqualCellValues)

const IncidentsTable = ({layers, featuresState}) => {
    const {t} = useTranslation(["webgis", "enums"]);
    const {incidentSelectedState} = featuresState;
    const {selectedFeatures, handleFeatureDeSelect} = incidentSelectedState;

    const {pagination, setPagination, sorting, onSortingChange, fetchDataOptions, goToFirstPage} = useDataTable();

    const params = useMemo(() => {
        return {
            ...formatFilterForAPI.incidents(layers),
            id__in: selectedFeatures.map(f => f.properties.id),
            ...fetchDataOptions,
            page: 1,
            page_size: 15
        }
    }, [layers, fetchDataOptions, selectedFeatures]);

    const {data, isLoading} = useIncidentsQuery(params);
    const incidentRegistryQuery = useIncidentRegistryQuery();

    const rows = data?.rows ?? [];
    const pageCount = data?.page_count ?? -1;

    useEffect(() => {
        goToFirstPage();
    }, [layers.commonFilters, goToFirstPage]);

    const columns = useMemo(() => [
        {
            accessorKey: 'date',
            cell: props => format(parseISO(props.getValue()), "dd/MM/yyyy"),
            header: () => <span>{t("incidentsTable.date")}</span>,
            enableSorting: true,
            meta: { width: "7rem", align: "left" }
        },
        {
            accessorKey: 'provinceAbbreviation',
            cell: props => props.getValue(),
            header: () => <span>{t("incidentsTable.provinceId")}</span>,
            enableSorting: true,
            meta: { width: "5rem", align: "left" }
        },
        {
            accessorKey: 'locationName',
            cell: props => props.getValue(),
            header: () => <span>{t("incidentsTable.locationName")}</span>,
            enableSorting: true,
            meta: { width: "10rem", align: "left" }
        },
        {
            accessorKey: 'incidentCategory',
            cell: props => <IncidentCategory cell={props}/>,
            header: () => <span>{t("incidentsTable.incidentCategory")}</span>,
            enableSorting: false,
            meta: { width: "10rem", align: "center" }
        },
        {
            accessorKey: 'incidentSubcategory',
            cell: props => <IncidentSubcategory cell={props}/>,
            header: () => <span>{t("incidentsTable.incidentSubcategory")}</span>,
            enableSorting: false,
            meta: { width: "10rem", align: "center" }
        },
        {
            accessorKey: 'numberOfPeopleInvolved',
            cell: props => props.getValue(),
            header: () => <span>{t("incidentsTable.numberOfPeopleInvolved")}</span>,
            enableSorting: true,
            meta: { width: "3rem", align: "center" }
        },
        {
            accessorKey: 'numberOfPeopleCaught',
            cell: props => props.getValue(),
            header: () => <span>{t("incidentsTable.numberOfPeopleCaught")}</span>,
            enableSorting: true,
            meta: { width: "3rem", align: "center" }
        },
        {
            accessorKey: 'numberOfUninjured',
            cell: props => props.getValue(),
            header: () => <span>{t("incidentsTable.numberOfUninjured")}</span>,
            enableSorting: true,
            meta: { width: "3rem", align: "center" }
        },
        {
            accessorKey: 'numberOfInjured',
            cell: props => props.getValue(),
            header: () => <span>{t("incidentsTable.numberOfInjured")}</span>,
            enableSorting: true,
            meta: { width: "3rem", align: "center" }
        },
        {
            accessorKey: 'numberOfDeaths',
            cell: props => props.getValue(),
            header: () => <span>{t("incidentsTable.numberOfDeaths")}</span>,
            enableSorting: true,
            meta: { width: "3rem", align: "center" }
        },
        {
            accessorKey: 'notes',
            cell: props => <NoteCell cell={props}/>,
            header: () => <span>{t("incidentsTable.notes")}</span>,
            enableSorting: true,
            meta: { width: "10rem", align: "left" }
        },
    ], [t]);

    const table = useReactTable({
        data: rows,
        columns,
        pageCount,
        state: {
            pagination,
            sorting,
        },
        onPaginationChange: setPagination,
        onSortingChange: onSortingChange,
        getCoreRowModel: getCoreRowModel(),
        manualPagination: true,
        manualSorting: true,
        meta: {
            incidentRegistry: incidentRegistryQuery.data
        }
    });

    if (isLoading || incidentRegistryQuery.isLoading) return null;
    
    return (
        <MDBox>
            {selectedFeatures.length > 0 && (
                <FeatureSelectionChips
                    selectedFeatures={selectedFeatures}
                    onDeSelectFeature={handleFeatureDeSelect}
                    sx={{ mb: 2, px: 2 }}
                    getLabel={(feature) => {
                        let label = '';
                        if (feature && feature.properties && feature.properties.locationName) {
                            label = `${feature.properties.locationName}`;
                        } else {
                            label = `${feature.properties.id}`;
                        }
                        if (feature && feature.properties && feature.properties.date) {
                            label += ` - ${format(new Date(feature.properties.date), 'dd/MM/yyyy')}`;
                        }
                        return label;
                    }}
                />
            )}
            <DataTable table={table} showTotalEntries={false} />
        </MDBox>
    );
};

export default IncidentsTable;
